import { BarElement, CategoryScale, Chart as ChartJS, LinearScale, Tooltip } from "chart.js";
import dayjs from "dayjs";
import millify from "millify";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";
import { useTranslation } from "react-i18next";

import { KeepoLoader } from "../../../../components/KeepoLoader";
import { localeOf } from "../../../../services/i18ln/format";
import { getLinkStats, LinkStats as Stats, LinkStatsItem } from "../../../../services/links";
import { SummaryList } from "../../../Statistics/components/SummaryList";
import { useBarOptions, useMillifyOptions } from "../../../Statistics/utils";
import { PeriodFilter } from "../PeriodFilter";
import { Lists, Panel, Total } from "./styles";

// Tooltip: the day's total on a column under the mouse (the Keepos' analytics tooltip, which needs it registered)
ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip);

const prefix = "pages.links.stats";

const PERIODS = ["7", "30", "90"] as const;
type Period = (typeof PERIODS)[number];

/**
 * The analytics of a Keepo Link over the last 7, 30 or 90 days: its clicks in all and each day, and where they came
 * from (countries, cities, devices, systems, sites). The same chart and lists as the Keepos' analytics.
 */
export const LinkStats = ({ linkId }: { linkId: string }) => {
  const { t, i18n } = useTranslation();
  const [period, setPeriod] = useState<Period>("30");
  const [stats, setStats] = useState<Stats>();
  const [hasError, setError] = useState(false);
  const millifyOptions = useMillifyOptions();
  const barOptions = useBarOptions(millifyOptions);
  const formatNumber = useCallback((value: number) => millify(value, millifyOptions), [millifyOptions]);
  // the Keepos' chart, its tooltip saying the column's clicks ("Dia 09/10 – 34 cliques")
  const chartOptions = useMemo(
    () => ({
      ...barOptions,
      maintainAspectRatio: false,
      plugins: {
        ...barOptions.plugins,
        tooltip: {
          ...barOptions.plugins.tooltip,
          callbacks: {
            ...barOptions.plugins.tooltip.callbacks,
            label: ({ raw }: { raw: unknown }) =>
              t("pages.links.clicks", {
                count: Number(raw),
                value: Number(raw).toLocaleString(localeOf(i18n.language)),
              }),
          },
        },
      },
    }),
    [barOptions, t, i18n.language]
  );

  const load = useCallback(() => {
    let current = true;
    setStats(undefined);
    setError(false);
    const to = dayjs();
    getLinkStats(linkId, to.subtract(Number(period) - 1, "day").format("YYYY-MM-DD"), to.format("YYYY-MM-DD"))
      .then(loaded => current && setStats(loaded))
      .catch(() => current && setError(true));
    return () => {
      current = false;
    };
  }, [linkId, period]);

  useEffect(load, [load]);

  // the countries in the person's language (Brasil), by their code
  const countryNames = useMemo(() => {
    try {
      return new Intl.DisplayNames([localeOf(i18n.language)], { type: "region" });
    } catch {
      return undefined;
    }
  }, [i18n.language]);

  const named = (items: LinkStatsItem[], name: (item: LinkStatsItem) => string) =>
    items.map(item => ({ value: name(item), count: item.clicks }));
  const unknownOr = (value: string) => (value === "unknown" ? t(`${prefix}.unknown`) : value);

  const chart = stats?.days.length
    ? {
        labels: stats.days.map(day => dayjs(day.date).format("DD/MM")),
        datasets: [
          { data: stats.days.map(day => day.clicks), backgroundColor: "#a3ffc8", borderWidth: 0, minBarLength: 1 },
        ],
      }
    : undefined;

  return (
    <Panel aria-labelledby="link-stats-title">
      <div className="head">
        <h2 id="link-stats-title">{t(`${prefix}.title`)}</h2>
        <PeriodFilter<Period>
          label={t(`${prefix}.period`)}
          value={period}
          options={PERIODS.map(option => ({ value: option, label: t(`${prefix}.periods.${option}`) }))}
          onChange={setPeriod}
        />
      </div>

      {hasError && (
        <p role="alert" className="note">
          {t(`${prefix}.error`)}{" "}
          <button type="button" onClick={load}>
            {t(`${prefix}.retry`)}
          </button>
        </p>
      )}

      {!stats && !hasError && (
        <div className="loading" role="status" aria-label={t(`${prefix}.loading`)}>
          <KeepoLoader size={24} color="var(--color-neutral-white)" />
        </div>
      )}

      {stats && (
        <>
          <Total>
            {/* the exact number (1.033), not rounded as in the lists (1 mil) */}
            <strong>{stats.clicks.toLocaleString(localeOf(i18n.language))}</strong>
            <span>{t(`${prefix}.total`)}</span>
          </Total>

          {stats.clicks === 0 ? (
            <p className="note">{t(`${prefix}.empty`)}</p>
          ) : (
            <>
              {chart && (
                <figure className="chart" aria-label={t(`${prefix}.chart`)}>
                  <Bar
                    //@ts-ignore the Keepos' analytics options (a plain object, typed loosely there)
                    options={chartOptions}
                    data={chart}
                  />
                </figure>
              )}
              <Lists>
                <SummaryList
                  title={t(`${prefix}.countries`)}
                  values={named(stats.countries, item =>
                    item.value === "unknown" || !item.code
                      ? unknownOr(item.value)
                      : (countryNames?.of(item.code) ?? item.value)
                  )}
                  formatterNumber={formatNumber}
                />
                <SummaryList
                  title={t(`${prefix}.cities`)}
                  values={named(stats.cities, item => unknownOr(item.value))}
                  formatterNumber={formatNumber}
                />
                <SummaryList
                  title={t(`${prefix}.devices`)}
                  values={named(stats.devices, item => t(`${prefix}.device.${item.value}`, item.value))}
                  formatterNumber={formatNumber}
                />
                <SummaryList
                  title={t(`${prefix}.systems`)}
                  values={named(stats.systems, item => unknownOr(item.value))}
                  formatterNumber={formatNumber}
                />
                <SummaryList
                  title={t(`${prefix}.sources`)}
                  values={named(stats.sources, item => (item.value === "direct" ? t(`${prefix}.direct`) : item.value))}
                  formatterNumber={formatNumber}
                />
              </Lists>
            </>
          )}
        </>
      )}
    </Panel>
  );
};
